/**
 * Global Realtime hook for intelligent query invalidation.
 * 
 * This hook listens to database changes and invalidates ONLY fresh data queries,
 * never the static data cache which has long TTL.
 * 
 * Tables listened:
 * - concrete_pours (filtered by site_id)
 * - concrete_deliveries (via pour relationship)
 * - delivery_incidents (INSERT only)
 * - work_progress (UPDATE only)
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

interface UseGlobalRealtimeOptions {
  enabled?: boolean;
}

export function useGlobalRealtime(
  siteId: string | null | undefined,
  options: UseGlobalRealtimeOptions = {}
) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const lastInvalidationRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled || !siteId) {
      return;
    }

    // Debounce invalidations to prevent rapid-fire updates
    const DEBOUNCE_MS = 500;

    const invalidateFreshQueries = () => {
      const now = Date.now();
      if (now - lastInvalidationRef.current < DEBOUNCE_MS) {
        return;
      }
      lastInvalidationRef.current = now;

      // Invalidate fresh data queries only
      // NEVER invalidate ['site-static-data'] - that's long-cached
      queryClient.invalidateQueries({ queryKey: ["site-fresh-data", siteId] });
      queryClient.invalidateQueries({ queryKey: ["chefStats"] });
      queryClient.invalidateQueries({ queryKey: ["site-betonnages", siteId] });
      
    };

    // Create a single channel for all site-related changes
    const channel = supabase
      .channel(`global-realtime-${siteId}`)
      
      // Listen to concrete_pours changes (filtered by site_id)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "concrete_pours",
          filter: `site_id=eq.${siteId}`,
        },
        (payload) => {
          const newRecord = payload.new as Record<string, unknown> | null;
          const oldRecord = payload.old as Record<string, unknown> | null;
          const recordId = (newRecord?.id as string) || (oldRecord?.id as string);
          invalidateFreshQueries();
        }
      )
      
      // Listen to concrete_deliveries changes
      // We can't filter by site_id directly, so we check the pour relationship
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "concrete_deliveries",
        },
        async (payload) => {
          // Get the pour to check if it belongs to our site
          const newRecord = payload.new as Record<string, unknown> | null;
          const oldRecord = payload.old as Record<string, unknown> | null;
          const pourId = (newRecord?.concrete_pour_id as string) || 
                         (oldRecord?.concrete_pour_id as string);
          
          if (!pourId) return;

          // Check cache first - if we have the pour in our chefStats, it's relevant
          const chefStatsData = queryClient.getQueryData(["chefStats"]) as { dailyOrders?: { id: string }[] } | undefined;
          const isRelevant = chefStatsData?.dailyOrders?.some((order) => order.id === pourId);

          if (isRelevant) {
            invalidateFreshQueries();
          }
        }
      )
      
      // Listen to delivery_incidents (INSERT and UPDATE only)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "delivery_incidents",
        },
        (payload) => {
          invalidateFreshQueries();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "delivery_incidents",
        },
        (payload) => {
          // Only care about resolution status changes
          const oldResolved = (payload.old as { resolved?: boolean })?.resolved;
          const newResolved = (payload.new as { resolved?: boolean })?.resolved;
          
          if (oldResolved !== newResolved) {
            invalidateFreshQueries();
          }
        }
      )
      
      // Listen to work_progress updates
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "work_progress",
        },
        (payload) => {
          // Check if this work item belongs to our site
          // For now, invalidate - the query will filter appropriately
          invalidateFreshQueries();
        }
      )
      
      .subscribe((status) => {
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [siteId, enabled, queryClient]);

  return {
    isSubscribed: !!channelRef.current,
  };
}

// For company-level realtime (multiple sites)
export function useCompanyRealtime(
  siteIds: string[] | undefined,
  options: UseGlobalRealtimeOptions = {}
) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const lastInvalidationRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled || !siteIds?.length) {
      return;
    }

    const DEBOUNCE_MS = 1000; // Longer debounce for company-level

    const invalidateCompanyQueries = () => {
      const now = Date.now();
      if (now - lastInvalidationRef.current < DEBOUNCE_MS) {
        return;
      }
      lastInvalidationRef.current = now;

      queryClient.invalidateQueries({ queryKey: ["director-stats"] });
      queryClient.invalidateQueries({ queryKey: ["company-fresh-data"] });
      
    };

    // For company-level, we listen to all pours and filter client-side
    const channel = supabase
      .channel("company-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "concrete_pours",
        },
        (payload) => {
          const pourSiteId = (payload.new as { site_id?: string })?.site_id || 
                             (payload.old as { site_id?: string })?.site_id;
          
          if (pourSiteId && siteIds.includes(pourSiteId)) {
            invalidateCompanyQueries();
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [siteIds, enabled, queryClient]);

  return {
    isSubscribed: !!channelRef.current,
  };
}
